import {formatInTimezone} from "@/composables/useTimezone";
import {validYmd} from "@/components/drivers/shifts/shiftFormat";

export const INSPECTION_PRESETS = [
    {value: "today", title: "Today"},
    {value: "last_7", title: "Last 7 days"},
    {value: "last_30", title: "Last 30 days"},
    {value: "custom", title: "Custom"},
    {value: "all", title: "All dates"},
];
const presetValues = INSPECTION_PRESETS.map((p) => p.value);

export const RESULTS = [
    {value: "passed", title: "Passed", color: "success"},
    {value: "defects", title: "Has defects", color: "warning"},
    {value: "failed", title: "Failed", color: "error"},
];

export const INSPECTION_DEFECT_FILTERS = [
    {value: "open_all", title: "Open or in repair"},
    {value: "open", title: "Open"},
    {value: "in_repair", title: "In repair"},
    {value: "resolved", title: "Resolved"},
];

export const BOARD_STATUSES = [
    ...INSPECTION_DEFECT_FILTERS,
    {value: "all", title: "All defects"},
];

export const SEVERITIES = [
    {value: "critical", title: "Critical"},
    {value: "major", title: "Major"},
    {value: "minor", title: "Minor"},
];

export const TYPES = [
    {value: "pre", title: "Pre-trip"},
    {value: "post", title: "Post-trip"},
];

export const GPS_FILTERS = [
    {value: "", title: "Any"},
    {value: "1", title: "Yes"},
    {value: "0", title: "No"},
];

export const statusColor = (s) => ({passed: "success", defects: "warning", failed: "error"}[s] || "grey");
export const statusLabel = (s) => RESULTS.find((r) => r.value === s)?.title || s;
export const sevLabel = (s) => SEVERITIES.find((x) => x.value === s)?.title || s;
export const defectStatusColor = (s) => ({open: "error", in_repair: "warning", resolved: "success"}[s] || "grey");
export const defectStatusLabel = (s) => ({open: "Open", in_repair: "In repair", resolved: "Resolved"}[s] || s);
export const sevColor = (s) => ({critical: "error", major: "warning", minor: "grey"}[s] || "grey");
export const typeLabel = (t) => (t === "post" ? "Post-trip" : "Pre-trip");
export const titleOf = (list, v) => list.find((x) => x.value === v)?.title || v;

export const RESOLUTIONS = [
    {value: "repaired", title: "Repaired"},
    {value: "no_repair_needed", title: "No repair needed"},
];
export const resolutionLabel = (r) => RESOLUTIONS.find((x) => x.value === r)?.title || "";

// Days an unresolved defect has been open; past this it shows red.
export const OPEN_TOO_LONG_DAYS = 7;
export const openDays = (createdAt, resolvedAt) => {
    const start = Date.parse(createdAt);
    if (Number.isNaN(start)) return null;
    const end = resolvedAt ? Date.parse(resolvedAt) : Date.now();
    return Math.max(0, Math.floor((end - start) / 86400000));
};
export const openFor = (createdAt, resolvedAt) => {
    const d = openDays(createdAt, resolvedAt);
    if (d === null) return "-";
    return d < 1 ? "< 1 d" : `${d} d`;
};

export const userName = (u) => (u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() : "");

export const driverName = (driver) => {
    const u = driver?.user;
    return u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() || "-" : "-";
};

// Times in the fleet's zone, the one the API cuts days in.
export const fmtDateTime = (ts, tz) => ts
    ? formatInTimezone(ts, {year: undefined, month: "short", day: "numeric", second: undefined}, tz)
    : "";
export const fmtDate = (ts, tz) => ts
    ? formatInTimezone(ts, {month: "short", day: "numeric", hour: undefined, minute: undefined, second: undefined}, tz)
    : "";

// ---- URL query ---------------------------------------------------------------

export const one = (v) => (Array.isArray(v) ? v[0] : v) ?? "";
export const many = (v) => (v === undefined || v === null || v === "" ? [] : (Array.isArray(v) ? v : [v]));
export const ids = (v) => many(v).filter((x) => /^\d{1,18}$/.test(String(x))).map(Number);
export const pick = (v, allowed, dflt) => (allowed.includes(one(v)) ? one(v) : dflt);
export const pickPreset = (v, dflt) => pick(v, presetValues, dflt);
export const ymdOr = (v) => (validYmd(one(v)) ? one(v) : "");
export const pageOf = (v) => (/^\d{1,6}$/.test(one(v)) && Number(one(v)) > 0 ? Number(one(v)) : 1);

// Both lists write the URL in the same tick; one replace at a time, each
// built on the query the last one left, so neither clobbers the other.
// Resolves once this patch is in the URL.
let pending = null;
let chain = Promise.resolve();
export const patchQuery = (router, patch) => {
    const first = pending === null;
    pending = {...(pending || {}), ...patch};
    if (!first) return chain;
    chain = chain.then(() => {
        const p = pending;
        pending = null;
        const q = {...router.currentRoute.value.query};
        for (const [k, v] of Object.entries(p)) {
            if (v === undefined || v === null || v === "" || (Array.isArray(v) && !v.length)) delete q[k];
            else q[k] = Array.isArray(v) ? v.map(String) : String(v);
        }
        return router.replace({query: q}).catch(() => {});
    });
    return chain;
};

// The query keys a list owns, normalised, to tell its own writes from a
// change that came from outside (the back button, a link).
export const sameQuery = (query, mine) => Object.entries(mine).every(([k, v]) => {
    const want = v === undefined || v === null || v === "" ? [] : many(v).map(String);
    const have = many(query[k]).map(String);
    return want.length === have.length && want.every((x, i) => x === have[i]);
});

// Vehicles, deleted ones included: records stay on trucks deleted since.
export const vehicleOptions = (list) => (Array.isArray(list) ? list : [])
    .map((a) => {
        const name = a.label || `Vehicle ${a.id}`;
        const note = a.deleted_at ? " (deleted)" : (a.status && a.status !== "enabled" ? " (disabled)" : "");
        return {id: a.id, label: name + note};
    })
    .sort((a, b) => a.label.localeCompare(b.label));

export const driverOptions = (list) => (Array.isArray(list) ? list : [])
    .map((d) => ({id: d.id, name: `${d.first_name || ""} ${d.last_name || ""}`.trim() || `Driver ${d.id}`}))
    .sort((a, b) => a.name.localeCompare(b.name));
